import {
  Button,
  CloseButton,
  Combobox,
  deepMerge,
  Text,
  useCombobox,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useContractsQueries } from '../../lib/queries/contracts';
import { Contract } from '../../lib/types/contracts';

import { filterOrganizationColumns, usePermissions } from '@auth/data-access';
import {
  FORM_STATUS,
  formatDate,
  useFiltersStore,
  useFiltersTableOptions,
} from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  StatusCell,
  StatusColumnFilter,
  TsrButtonLink,
} from '@ui';

const CLAUSE_OPTIONS = [
  { value: 'new', labelKey: 'clauses.new' },
  { value: 'gp', labelKey: 'clauses.gp' },
] as const;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ContractsTableProps {}

export const ContractsTable: FC<ContractsTableProps> = () => {
  const { t } = useTranslation();
  const { showOrganizationColumn } = usePermissions();
  const contractsQueries = useContractsQueries();
  const page = useFiltersStore.use['contracts']().pagination.pageIndex + 1;
  const sorting = useFiltersStore.use['contracts']().sorting;
  const filters = useFiltersStore.use['contracts']().filters;
  const filtersTableOptions = useFiltersTableOptions<Contract>('contracts');

  const contracts = useQuery({
    ...contractsQueries.list({ page, sorting, filters }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<MRT_ColumnDef<Contract>[]>(
    () =>
      filterOrganizationColumns(
        [
      {
        accessorKey: 'farm.name',
        header: t('common.company_name'),
        Cell: ({ cell, row }) => {
          return (
            <TsrButtonLink
              fullWidth
              justify="start"
              maw={250}
              ml="calc(var(--button-padding-x-compact-md) * -1)"
              params={{ contractId: row.original.id.toString() }}
              size="compact-md"
              td="underline"
              to="/os/contracts/$contractId"
              tt="capitalize"
              variant="subtle"
            >
              <Text span truncate="end">
                {cell.getValue<string>()}
              </Text>
            </TsrButtonLink>
          );
        },
      },
      {
        id: 'organizations',
        accessorFn: (row) => formatOrganizationNames(row.organizations),
        header: t('common.properties.organization'),
        Cell: ({ row }) => (
          <OrganizationsList organizations={row.original.organizations} />
        ),
      },
      {
        accessorKey: 'clause',
        header: t('common.properties.clause'),
        Cell: ({ cell }) => {
          const clause = cell.getValue<string>();
          return t(`clauses.${clause}`);
        },
        Filter: ({ column }) => {
          const { t } = useTranslation();
          const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
          });
          const value = column.getFilterValue() as string | undefined;
          return (
            <Combobox
              onOptionSubmit={(val) => {
                column.setFilterValue(val || null);
                combobox.closeDropdown();
              }}
              position="bottom-start"
              store={combobox}
            >
              <Combobox.Target>
                <Button
                  color="gray"
                  fullWidth
                  justify="space-between"
                  onClick={() => combobox.toggleDropdown()}
                  px="sm"
                  rightSection={
                    value ? (
                      <CloseButton
                        aria-label={t('common.clear')}
                        onClick={(e) => {
                          e.stopPropagation();
                          column.setFilterValue(null);
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                        size="sm"
                      />
                    ) : (
                      <Combobox.Chevron />
                    )
                  }
                  tt="initial"
                  variant="light"
                >
                  <Text c={value ? undefined : 'var(--mantine-color-placeholder)'}>
                    {value ? t(`clauses.${value}`) : t('table.columns.clause-filter-placeholder')}
                  </Text>
                </Button>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>
                  {CLAUSE_OPTIONS.map((opt) => (
                    <Combobox.Option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          );
        },
      },
      {
        accessorKey: 'consent_form.status',
        header: t('common.properties.consent_form_status'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          if (!status) {
            return '-';
          }

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
      {
        accessorKey: 'engaged_fields_status',
        header: t('common.properties.engaged_fields_status'),
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
        ],
        showOrganizationColumn('list'),
      ),
    [t, showOrganizationColumn],
  );

  const tableOptions: MRT_TableOptions<Contract> = deepMerge(
    {
      data: contracts.data?.results ?? [],
      rowCount: contracts.data?.count ?? 0,
      columns,

      state: {
        showLoadingOverlay: contracts.isFetching && contracts.isPlaceholderData,
      },

      initialState: {
        showColumnFilters: true,
      },

      mantinePaperProps: {
        h: '100%',
      },
    },
    filtersTableOptions,
  );

  return <PfTable<Contract> options={tableOptions} />;
};

export default ContractsTable;
